/**
 * Key combinations as text: modifiers in a fixed order then the key,
 * e.g. "F10", "Ctrl+Down", "Shift+Ctrl+E" is stored as "Ctrl+Shift+E".
 */
const KEY_NAMES: Record<string, string> = {
  ArrowUp: 'Up',
  ArrowDown: 'Down',
  ArrowLeft: 'Left',
  ArrowRight: 'Right',
  ' ': 'Space',
  Esc: 'Escape',
};
const MODIFIER_KEYS = new Set(['Control', 'Shift', 'Alt', 'Meta', 'AltGraph', 'CapsLock']);

/** "Shift+Ctrl+E" -> "Ctrl+Shift+E" so stored and pressed combos compare equal */
export const normalizeCombo = (combo: string | null | undefined): string | null => {
  if (!combo) return null;
  const parts = combo.split('+').map((p) => p.trim()).filter(Boolean);
  if (!parts.length) return null;
  const key = parts[parts.length - 1];
  const mods = new Set(parts.slice(0, -1).map((m) => m.toLowerCase()));
  const out: string[] = [];
  if (mods.has('ctrl') || mods.has('control')) out.push('Ctrl');
  if (mods.has('alt')) out.push('Alt');
  if (mods.has('shift')) out.push('Shift');
  if (mods.has('meta') || mods.has('cmd')) out.push('Meta');
  out.push(key.length === 1 ? key.toUpperCase() : KEY_NAMES[key] ?? key);
  return out.join('+');
};

/** combo of a keydown, or null for a lone modifier press */
export const comboFromEvent = (e: KeyboardEvent | React.KeyboardEvent): string | null => {
  if (MODIFIER_KEYS.has(e.key)) return null;
  // letters/digits by physical key, so Shift/Alt layouts still give "E", "1"
  let key = e.key;
  if (/^Key[A-Z]$/.test(e.code)) key = e.code.slice(3);
  else if (/^Digit\d$/.test(e.code)) key = e.code.slice(5);
  const parts: string[] = [];
  if (e.ctrlKey) parts.push('Ctrl');
  if (e.altKey) parts.push('Alt');
  if (e.shiftKey) parts.push('Shift');
  if (e.metaKey) parts.push('Meta');
  parts.push(key);
  return normalizeCombo(parts.join('+'));
};

/** keys the browser or OS keeps, or that would break typing */
export const reservedReason = (combo: string): string | null => {
  const blocked: Record<string, string> = {
    F11: 'full screen in the browser',
    F12: 'opens the browser developer tools',
    'Ctrl+W': 'closes the browser tab',
    'Ctrl+T': 'opens a browser tab',
    'Ctrl+N': 'opens a browser window',
    'Ctrl+Shift+N': 'opens a private window',
    'Ctrl+Shift+T': 'reopens a browser tab',
    'Ctrl+Tab': 'switches browser tabs',
    'Ctrl+Shift+Tab': 'switches browser tabs',
    'Alt+F4': 'closes the browser window',
    'Ctrl+Shift+I': 'opens the browser developer tools',
    'Ctrl+Shift+J': 'opens the browser console',
    Tab: 'moves between fields',
    'Shift+Tab': 'moves between fields',
    Enter: 'confirms fields and adds items',
    Escape: 'closes lists and dialogs',
    Backspace: 'deletes text',
    Delete: 'deletes text',
    'Ctrl+C': 'copy',
    'Ctrl+V': 'paste',
    'Ctrl+X': 'cut',
    'Ctrl+A': 'select all',
    'Ctrl+Z': 'undo',
  };
  if (blocked[combo]) return blocked[combo];
  const parts = combo.split('+');
  const key = parts[parts.length - 1];
  const hasCtrlOrAlt = parts.includes('Ctrl') || parts.includes('Alt') || parts.includes('Meta');
  if (!hasCtrlOrAlt && (key.length === 1 || key === 'Space')) return 'would type into fields';
  if (!hasCtrlOrAlt && (key === 'Left' || key === 'Right' || key === 'Home' || key === 'End'))
    return 'moves the cursor in fields';
  return null;
};

/** keys the browser may act on before the page sees them; allowed but flagged */
export const browserMayTake = (combo: string): string | null =>
  ({
    F6: 'Some browsers move focus to the address bar on F6. Check it works on your browser.',
    F5: 'F5 is the browser refresh outside entry screens.',
    F10: 'On Windows some browsers open their menu on F10. Check it works on your browser.',
  })[combo] ?? null;

export const displayCombo = (combo: string | null | undefined) => (combo ? combo.replace(/\+/g, ' + ') : '—');
